import { getScoreTitle, gradingTypeSupportsGeneralScore, gradingTypeSupportsSubgrades } from "./gradingTypes";

export const subgradeFields = [
    { key: "surface", payloadKey: "notaSurface", label: "Surface" },
    { key: "edges", payloadKey: "notaEdge", label: "Edges" },
    { key: "centering", payloadKey: "notaCenter", label: "Centering" },
    { key: "corners", payloadKey: "notaCorner", label: "Corners" }
];

const hasValue = (value) => {
    return value !== "" && value !== null && value !== undefined;
}

const allowedAutographNotes = ["Autentico", "8", "9", "10"];

const getProductGradingType = (value) => {
    if (!value || typeof value !== "object") {
        return "";
    }
    return value?.gradingType || value?.product?.gradingType || value?.data?.gradingType || "";
}

const getProductPreLacreFormat = (value) => {
    if (!value || typeof value !== "object") {
        return "";
    }
    return value?.preLacreFormat || value?.product?.preLacreFormat || value?.data?.preLacreFormat || "";
}

const getProductAutographEvaluation = (value) => {
    if (!value || typeof value !== "object") {
        return undefined;
    }
    return value?.autographEvaluation ?? value?.product?.autographEvaluation ?? value?.data?.autographEvaluation;
}

const getSerialGradingType = (serial, product) => {
    return (
        getProductGradingType(serial?.product) ||
        getProductGradingType(serial?.productData) ||
        getProductGradingType(product) ||
        serial?.gradingType
    );
}

const getSerialPreLacreFormat = (serial, product) => {
    return (
        getProductPreLacreFormat(serial?.product) ||
        getProductPreLacreFormat(serial?.productData) ||
        getProductPreLacreFormat(product) ||
        serial?.preLacreFormat
    );
}

const getSerialAutographEvaluation = (serial, product) => {
    return (
        getProductAutographEvaluation(serial?.product) ??
        getProductAutographEvaluation(serial?.productData) ??
        getProductAutographEvaluation(product) ??
        serial?.autographEvaluation
    );
}

const stringifyGrade = (value) => {
    if (value === null || value === undefined) {
        return value;
    }
    return value?.toString();
}

const normalizeAutographNote = (value) => {
    return stringifyGrade(value)?.trim();
}

export const getSerialGradeApplicability = (serial, product) => {
    const gradingType = getSerialGradingType(serial, product);
    const preLacreFormat = getSerialPreLacreFormat(serial, product);
    const autographEvaluation = getSerialAutographEvaluation(serial, product);
    return {
        supportsGeneralScore: gradingTypeSupportsGeneralScore(gradingType),
        supportsSubgrades: gradingTypeSupportsSubgrades(gradingType, preLacreFormat),
        supportsAutographNote: !!autographEvaluation
    };
}

export const getVisibleGradeFields = (serial, product) => {
    const { supportsGeneralScore, supportsSubgrades, supportsAutographNote } = getSerialGradeApplicability(serial, product);
    return {
        generalScore: supportsGeneralScore ? { key: "generalScore", label: "Nota Geral", value: serial?.generalScore } : null,
        subgrades: supportsSubgrades ? subgradeFields.map(field => ({ key: field.key, label: field.label, value: serial?.[field.key] })) : [],
        autographNote: supportsAutographNote ? { key: "notaAutografo", label: "Nota Autógrafo", value: serial?.notaAutografo ?? serial?.autographNote } : null
    };
}

export const validateProductAutographNotes = (productData) => {
    if (productData?.autographEvaluation !== true) {
        return { ok: true, missingCount: 0, invalidCount: 0 };
    }

    const serials = Array.isArray(productData?.serials) ? productData.serials : [];
    const totals = serials.reduce((acc, serial) => {
        const autographNote = normalizeAutographNote(serial?.notaAutografo ?? serial?.autographNote);
        if (!hasValue(autographNote)) {
            return { ...acc, missingCount: acc.missingCount + 1 };
        }
        if (!allowedAutographNotes.includes(autographNote)) {
            return { ...acc, invalidCount: acc.invalidCount + 1 };
        }
        return acc;
    }, { missingCount: 0, invalidCount: 0 });

    return {
        ok: totals.missingCount === 0 && totals.invalidCount === 0,
        missingCount: totals.missingCount,
        invalidCount: totals.invalidCount
    };
}

export const renderApplicableGradeValue = (value) => {
    return value === "" || value === null || value === undefined ? "Pendente" : value;
}

export const renderGradeValueForTable = (serial, key, product) => {
    const { supportsGeneralScore, supportsSubgrades, supportsAutographNote } = getSerialGradeApplicability(serial, product);
    if (key === "generalScore" && !supportsGeneralScore) {
        return "-";
    }
    if (subgradeFields.some(field => field.key === key) && !supportsSubgrades) {
        return "-";
    }
    if ((key === "notaAutografo" || key === "autographNote") && !supportsAutographNote) {
        return "-";
    }
    if (key === "notaAutografo" || key === "autographNote") {
        return renderApplicableGradeValue(serial?.notaAutografo ?? serial?.autographNote);
    }
    return renderApplicableGradeValue(serial?.[key]);
}

export const buildApplicableGradePayload = (serial, product) => {
    const { supportsGeneralScore, supportsSubgrades, supportsAutographNote } = getSerialGradeApplicability(serial, product);
    const payload = {};

    if (supportsSubgrades) {
        subgradeFields.forEach(field => {
            const value = stringifyGrade(serial?.[field.key]);
            if (hasValue(value)) {
                payload[field.payloadKey] = value;
            }
        });
    }

    if (supportsGeneralScore) {
        const generalScore = stringifyGrade(serial?.generalScore);
        if (hasValue(generalScore)) {
            payload.notaGeral = generalScore;
            payload.nome_nota = getScoreTitle(serial?.generalScore);
        }
    }

    if (supportsAutographNote) {
        const autographNote = stringifyGrade(serial?.notaAutografo ?? serial?.autographNote);
        if (hasValue(autographNote)) {
            payload.notaAutografo = autographNote;
        }
    }

    return payload;
}
